import * as BasketApi from '@/api/basket';
import { queries } from '@/api/queries';
import { NormalizedBasket } from '@/model';
import { MutationConfig } from '@/types';
import { BasketType } from '@/types/basket';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useCloned } from '@vueuse/core';

type CreateVoucherOptions = {
  config?: MutationConfig<typeof BasketApi.removeItem>;
};

const useRemoveItem = (basketType: BasketType, { config }: CreateVoucherOptions = {}) => {
  const queryClient = useQueryClient();
  const basketItemProperty = basketType === 'TakeawayBasket' ? 'takeawayBasketItem' : 'basketItem';
  return useMutation({
    onMutate: ({ basketId, itemId }) => {
      queryClient.cancelQueries(queries.baskets.byId(basketId));
      const oldBasketItem = queryClient.getQueryData<NormalizedBasket>(
        queries.baskets.byId(basketId).queryKey
      )?.[basketItemProperty]?.[itemId];

      queryClient.setQueryData<NormalizedBasket>(
        queries.baskets.byId(basketId).queryKey,
        basket => {
          const { cloned: tempBasket } = useCloned(basket, { manual: true });

          if (tempBasket.value?.[basketItemProperty]?.[itemId]) {
            delete tempBasket.value[basketItemProperty]![itemId];
          }

          return tempBasket.value;
        }
      );

      return { oldBasketItem };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (_, { basketId, itemId }, { oldBasketItem }: any) => {
      return queryClient.setQueriesData<NormalizedBasket>(
        queries.baskets.byId(basketId),
        basket => {
          if (basket?.[basketItemProperty]?.[itemId])
            basket[basketItemProperty]![itemId] = oldBasketItem;
          return basket;
        }
      );
    },
    onSuccess: async (result, { basketId }) => {
      queryClient.setQueryData<NormalizedBasket>(queries.baskets.byId(basketId).queryKey, result);
    },
    onSettled: (_, __, { basketId }) => {
      queryClient.invalidateQueries(queries.baskets.byId(basketId));
    },
    ...config,
    mutationFn: BasketApi.removeItem,
  });
};

export default useRemoveItem;
